// Local type definitions to avoid circular dependencies
interface ErrorBoundaryProps {
  title: string
  description: string
  errorMessage?: string
  showDetails?: boolean
}

// Simple logger implementation to avoid circular dependencies
const logger = {
  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    if (
      typeof window !== 'undefined'
        ? (import.meta as { env?: { DEV?: boolean } }).env?.DEV
        : process.env.NODE_ENV === 'development'
    ) {
      console.error(`[ERROR] ${message}`, error, context)
    }
  },
}

/**
 * Error handling utilities with consistent patterns across apps
 */

/**
 * Type guard to check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.toLowerCase().includes('network') ||
      error.message.toLowerCase().includes('fetch') ||
      error.message.toLowerCase().includes('connection') ||
      (error.name === 'TypeError' && error.message.includes('fetch'))
    )
  }
  return false
}

/**
 * Extracts a user-friendly error message from an error object
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>
    return (errorObj.message as string) || (errorObj.error as string) || 'An unknown error occurred'
  }

  return 'An unknown error occurred'
}

/**
 * Handles mutation errors by logging and showing a toast
 * Note: This function requires toast implementation in the consuming app
 */
export function handleMutationError(error: unknown, fallbackMessage: string): void {
  logger.error('Mutation error', error)

  const errorMessage = getErrorMessage(error)

  // In a real implementation, this would use a toast library
  // For now, we'll log the error and let the consuming app handle the toast
  console.error('Toast would show:', fallbackMessage, { description: errorMessage })

  // The consuming app should implement toast notifications like:
  // toast.error(fallbackMessage, { description: errorMessage, duration: 5000 })
}

/**
 * Handles query errors by returning a user-friendly message
 */
export function handleQueryError(error: unknown, fallbackMessage?: string): string {
  if (isNetworkError(error)) {
    return fallbackMessage || 'Unable to load data. Check your connection and try again.'
  }

  return fallbackMessage || getErrorMessage(error)
}

/**
 * Wraps async operations with error handling
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  fallbackMessage: string,
  onError?: (error: unknown) => void
): Promise<T | null> {
  try {
    return await operation()
  } catch (error) {
    handleMutationError(error, fallbackMessage)
    onError?.(error)
    return null
  }
}

/**
 * Creates error boundary fallback props for consistent error display
 */
export function createErrorBoundaryProps(error?: Error): ErrorBoundaryProps {
  const isDev =
    typeof window !== 'undefined'
      ? (import.meta as { env?: { DEV?: boolean } }).env?.DEV
      : process.env.NODE_ENV === 'development'
  const isNetwork = error ? isNetworkError(error) : false

  return {
    title: isNetwork ? 'Connection Error' : 'Something went wrong',
    description: isNetwork
      ? 'Please check your internet connection and try again.'
      : 'We encountered an error while loading this section. Please try again.',
    errorMessage: error?.message,
    showDetails: isDev && !!error?.message,
  }
}

/**
 * Error boundary component for React
 */
export interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export interface ErrorBoundaryComponentProps {
  children: React.ReactNode
  fallback?: (props: ErrorBoundaryProps) => React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/**
 * Hook for error boundary functionality
 */
export function useErrorHandler() {
  return {
    isNetworkError,
    getErrorMessage,
    handleMutationError,
    handleQueryError,
    withErrorHandling,
    createErrorBoundaryProps,
  }
}
